import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

const petalColors = [
  "hsl(350 58% 62%)",
  "hsl(350 55% 52%)",
  "hsl(350 52% 72%)",
  "hsl(150 13% 61%)",
  "hsl(350 45% 78%)",
];

// Different petal/flower SVG paths for variety
const petalSvgs = [
  // Teardrop petal
  (color: string, size: number) => (
    <svg width={size} height={size * 1.3} viewBox="0 0 16 22" fill="none">
      <path d="M8 0C8 0 0 10 0 16C0 19.3 3.6 22 8 22C12.4 22 16 19.3 16 16C16 10 8 0 8 0Z" fill={color} fillOpacity="0.7" />
    </svg>
  ),
  // Small 5-petal flower
  (color: string, size: number) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="5" r="3.5" fill={color} fillOpacity="0.6" />
      <circle cx="5" cy="10" r="3.5" fill={color} fillOpacity="0.6" />
      <circle cx="15" cy="10" r="3.5" fill={color} fillOpacity="0.6" />
      <circle cx="7" cy="15" r="3.5" fill={color} fillOpacity="0.6" />
      <circle cx="13" cy="15" r="3.5" fill={color} fillOpacity="0.6" />
      <circle cx="10" cy="10" r="2" fill="hsl(40 45% 70%)" fillOpacity="0.8" />
    </svg>
  ),
  // Leaf
  (color: string, size: number) => (
    <svg width={size * 0.7} height={size * 1.2} viewBox="0 0 12 20" fill="none">
      <path d="M6 0C6 0 0 6 0 14C0 18 2.7 20 6 20C9.3 20 12 18 12 14C12 6 6 0 6 0Z" fill={color} fillOpacity="0.6" />
    </svg>
  ),
  // Round petal
  (color: string, size: number) => (
    <svg width={size} height={size * 0.8} viewBox="0 0 16 12" fill="none">
      <ellipse cx="8" cy="6" rx="8" ry="6" fill={color} fillOpacity="0.65" />
    </svg>
  ),
];

interface PetalGroup {
  id: number;
  x: number;
  y: number;
  particles: { angle: number; distance: number; size: number; svgIdx: number; color: string; rotEnd: number }[];
}

let nextId = 0;

export function ClickSparkle() {
  const [groups, setGroups] = useState<PetalGroup[]>([]);

  const handleClick = useCallback((e: MouseEvent) => {
    const count = 6 + Math.floor(Math.random() * 4);
    const particles = Array.from({ length: count }, () => ({
      angle: Math.random() * 360,
      distance: 25 + Math.random() * 55,
      size: 8 + Math.random() * 10,
      svgIdx: Math.floor(Math.random() * petalSvgs.length),
      color: petalColors[Math.floor(Math.random() * petalColors.length)],
      rotEnd: (Math.random() - 0.5) * 360,
    }));

    const group: PetalGroup = { id: nextId++, x: e.clientX, y: e.clientY, particles };
    setGroups((prev) => [...prev, group]);
    setTimeout(() => setGroups((prev) => prev.filter((g) => g.id !== group.id)), 800);
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [handleClick]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[9999]" aria-hidden="true">
      <AnimatePresence>
        {groups.map((group) =>
          group.particles.map((p, i) => {
            const rad = (p.angle * Math.PI) / 180;
            const tx = Math.cos(rad) * p.distance;
            const ty = Math.sin(rad) * p.distance;
            return (
              <motion.div
                key={`${group.id}-${i}`}
                className="absolute"
                style={{ left: group.x, top: group.y }}
                initial={{ opacity: 1, x: 0, y: 0, scale: 1, rotate: 0 }}
                animate={{ opacity: 0, x: tx, y: ty, scale: 0.3, rotate: p.rotEnd }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.55 + Math.random() * 0.2, ease: "easeOut" }}
              >
                {petalSvgs[p.svgIdx](p.color, p.size)}
              </motion.div>
            );
          })
        )}
      </AnimatePresence>
    </div>
  );
}
